import Navbar from "~/components/navbar";
import {
  Accordion,
  AccordionItem,
  Card,
  CardBody,
  Link,
} from "@nextui-org/react";
import { MetaFunction } from "react-router";
import { LuUsers } from "react-icons/lu";

import { HiSparkles } from "react-icons/hi2";
import DotPattern from "~/components/dot-pattern";
import { getPublicAssetURL } from "~/utils/assets";
import { BiSupport } from "react-icons/bi";
import Form from "./form";
import { OG_METADATA } from "~/utils/constants";

export const meta: MetaFunction = () => {
  return [
    {
      title: "Become an expert",
    },
    {
      name: "description",
      content:
        "Join India's curated platform for premium service experts. Get discovered by quality clients, set your own schedule, and grow your business with AI-powered matching.",
    },
    ...OG_METADATA,
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "tribe.best",
        url: "https://tribe.best",
        logo: "https://tribe.best/apple-touch-icon.png",
        email: "hello@tribe.best",
        description:
          "tribe.best is a next-generation service discovery platform that connects quality-conscious consumers with India's finest service experts through an intelligent, AI-powered marketplace.",
        foundingDate: "2023",
        telephone: "+916360004054",
        contactPoint: [
          {
            "@type": "ContactPoint",
            email: "hello@tribe.best",
            telephone: "+916360004054",
          },
        ],
        legalName: "Speakleap Technologies Private Limited",
        numberOfEmployees: {
          "@type": "QuantitativeValue",
          value: 4,
        },
        address: [
          {
            "@type": "PostalAddress",
            streetAddress:
              "1st floor, SPD Plaza, 52, Jyoti Niwas College Rd, Koramangala",
            addressLocality: "Bengaluru",
            addressRegion: "Karnataka",
            postalCode: "560034",
            addressCountry: "India",
          },
        ],
      },
    },
  ];
};

export default function BecomeExpert() {
  return (
    <div className="w-full mx-auto">
      <Navbar />
      <section className="w-full relative pb-32">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 flex flex-col gap-8 md:gap-48">
          <div className="text-center z-10">
            <h2 className="text-5xl md:text-7xl font-bold text-foreground-100 mb-6 animate-fade-up animate-delay-500">
              Partner with tribe.best and grow your business
            </h2>
            <p className="text-xl md:text-2xl text-foreground-200 max-w-2xl mx-auto animate-fade-up animate-delay-[1200ms]">
              Join India&apos;s most innovative platform for service experts.
              Let AI-powered matching bring your ideal clients directly to you.
            </p>
          </div>
        </div>
        <img
          src={getPublicAssetURL("assets/become-expert/poster.jpeg")}
          alt="become an expert"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 w-full h-full bg-black bg-opacity-50 animate-fade duration-500"></div>
      </section>

      <section className="relative w-full -mt-40 px-4">
        <Card className="max-w-2xl mx-auto h-[470px] sm:h-[361px]">
          <CardBody>
            <Form />
          </CardBody>
        </Card>
      </section>

      <section className="relative w-full">
        <div className="max-w-7xl mx-auto flex flex-col gap-16 items-center px-4 md:px-8 py-16">
          <div className="text-center">
            <h3 className="text-3xl md:text-4xl font-semibold ">
              Why should you partner with{" "}
              <span className="text-primary">tribe.best</span>?
            </h3>
            <p className="text-lg">
              We&apos;re about people, quality, and excellence.
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-4 w-full">
            <Card className="md:basis-1/3">
              <CardBody className="p-6">
                <div className="bg-primary-100 rounded-full aspect-square flex justify-center items-center size-12 mb-6">
                  <LuUsers className="size-6 text-primary-500" />
                </div>
                <h4 className="text-xl font-semibold mb-2">
                  Attract new customers
                </h4>
                <p>
                  Reach a broader audience of quality-conscious customers who
                </p>
              </CardBody>
            </Card>
            <Card className="md:basis-1/3">
              <CardBody className="p-6">
                <div className="bg-primary-100 rounded-full aspect-square flex justify-center items-center size-12 mb-6">
                  <HiSparkles className="size-6 text-primary-500" />
                </div>
                <h4 className="text-xl font-semibold mb-2">
                  AI-Powered Matching
                </h4>
                <p>
                  Our intelligent algorithms analyze style, expertise, and past
                  success to create perfect customer-expert matches.
                </p>
              </CardBody>
            </Card>
            <Card className="md:basis-1/3">
              <CardBody className="p-6">
                <div className="bg-primary-100 rounded-full aspect-square flex justify-center items-center size-12 mb-6">
                  <BiSupport className="size-6 text-primary-500" />
                </div>
                <h4 className="text-xl font-semibold mb-2">Hotline Support</h4>
                <p>On-call support for any issues or growth consultations</p>
              </CardBody>
            </Card>
          </div>
          <p className="mt-4 max-w-2xl text-center text-xl font-medium">
            &quot;We believe exceptional results come from exceptional
            individuals. We&apos;ve built a platform that celebrates expertise,
            creativity, and the unique talent each professional brings to their
            craft.&quot;
          </p>
        </div>
      </section>

      <section className="relative w-full bg-gradient-to-b from-primary-100 via-primary-50 to-background">
        <DotPattern />
        <div className="max-w-7xl mx-auto flex flex-col gap-16 items-center px-4 md:px-8 py-16">
          <div className="text-center">
            <h3 className="text-3xl md:text-4xl font-semibold ">
              How <span className="text-primary">AI</span> finds the right
              clients for you
            </h3>
          </div>

          <div className="flex flex-col gap-4 w-full">
            <Card>
              <CardBody className="p-6 flex items-center flex-row gap-4">
                <div className="bg-primary-100 rounded-full aspect-square flex justify-center items-center size-10 mb-6">
                  <p className="text-primary-500 text-xl font-bold">1</p>
                </div>
                <div>
                  <h4 className="text-xl font-semibold mb-2">Style Analysis</h4>
                  <p>
                    AI studies your portfolio to understand your unique style
                    and specialties, ensuring perfect customer alignment.
                  </p>
                </div>
              </CardBody>
            </Card>
            <Card>
              <CardBody className="p-6 flex items-center flex-row gap-4">
                <div className="bg-primary-100 rounded-full aspect-square flex justify-center items-center size-10 mb-6">
                  <p className="text-primary-500 text-xl font-bold">2</p>
                </div>
                <div>
                  <h4 className="text-xl font-semibold mb-2">
                    Success Tracking
                  </h4>
                  <p>
                    Learns from successful projects to continuously improve your
                    matching accuracy and customer satisfaction.
                  </p>
                </div>
              </CardBody>
            </Card>
            <Card>
              <CardBody className="p-6 flex items-center flex-row gap-4">
                <div className="bg-primary-100 rounded-full aspect-square flex justify-center items-center size-10 mb-6">
                  <p className="text-primary-500 text-xl font-bold">3</p>
                </div>
                <div>
                  <h4 className="text-xl font-semibold mb-2">Smart Growth</h4>
                  <p>
                    Identifies growth opportunities and suggests ways to expand
                    your customer base and service offerings.
                  </p>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </section>

      {/* <section className="relative w-full bg-primary-800">
        <div className="max-w-7xl mx-auto flex flex-col gap-20 items-center px-4 md:px-8 py-16 pb-24">
          <h3 className="text-4xl text-foreground-100 font-semibold text-center">
            Expert success stories
          </h3>
        </div>
      </section> */}

      <section className="relative">
        <div className="flex flex-col items-center max-w-3xl mx-auto px-4 md:px-8 py-16 gap-16">
          <h3 className="text-3xl md:text-4xl font-medium">FAQs</h3>
          <Accordion variant="splitted">
            <AccordionItem
              key="details"
              title="What are the documents and details required to register as an expert on tribe.best?"
            >
              As of now, you do not need to provide any documents or details to
              register as an expert on tribe.best. Just your Instagram handle
              and your phone number.
            </AccordionItem>
            <AccordionItem
              key="time"
              title="How long does it take to get approved as an expert?"
            >
              Usually that happens in 48 hrs given that you have a good
              portfolio and a good profile.
            </AccordionItem>
            <AccordionItem
              key="fees"
              title="What is the onboarding fees and how do you charge?"
            >
              There is no onboarding fees as of now, we are very early stage and
              want to work with you to come up with a best possible plan.
            </AccordionItem>
            <AccordionItem
              key="contact"
              title="How can I get help if I am stuck?"
            >
              There are two ways to get help:
              <ul>
                <li>
                  <Link
                    underline="hover"
                    href="mailto:hello@tribe.best"
                    target="_blank"
                  >
                    Email
                  </Link>
                </li>
                <li>
                  <Link
                    underline="hover"
                    href="tel:+916360004054"
                    target="_blank"
                  >
                    Phone
                  </Link>
                </li>
              </ul>
              Since we are a very small team, might take a little longer to
              reply but we always respond.
            </AccordionItem>
            <AccordionItem key="payout" title="How do I get paid?">
              You get paid directly by the customer. We do not take any
              commission or fees. We are all about making right connections.
            </AccordionItem>
          </Accordion>
        </div>
      </section>
    </div>
  );
}
